/**
 * "Proefbericht" onderaan de lijst, alleen op staging (sessie 158).
 *
 * JP zet een klantbericht in van een nagespeelde klant (nk-*), per mail, chat
 * of WhatsApp. De server stuurt het door exact dezelfde inname als een echt
 * binnenkomend bericht, dus de motor pakt het op volgens de schakelaars en de
 * zaak verschijnt in de lijst zoals live. Er gaat niets naar buiten (de grendel
 * van de testomgeving).
 *
 * De knop bestaat alleen als de server de klantenlijst geeft: buiten staging
 * geeft die route 404 en toont dit onderdeel niets.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useState, type FormEvent } from "react";

import { apiClient } from "@/lib/api";

import { v6Sleutels } from "../sleutels";
import "./proefbericht.css";

export type ProefKanaal = "mail" | "chat" | "whatsapp";

export type ProefKlant = {
  id: string;
  sleutel: string;
  naam: string;
  email: string | null;
  mobiel: string | null;
  aantal_orders: number;
};

type ProefKlanten = { klanten: ProefKlant[]; kanalen: ProefKanaal[]; max_tekst: number };

const KANAAL_WOORD: Record<ProefKanaal, string> = {
  mail: "Mail",
  chat: "Chat",
  whatsapp: "WhatsApp",
};

export const proefApi = {
  async klanten(): Promise<ProefKlanten> {
    const { data } = await apiClient.get<ProefKlanten>("/api/werkplek/proefbericht/klanten");
    return data;
  },
  async zetIn(velden: {
    kanaal: ProefKanaal;
    klant_id: string;
    tekst: string;
    onderwerp?: string;
    foto?: File | null;
  }): Promise<void> {
    const form = new FormData();
    form.append("kanaal", velden.kanaal);
    form.append("klant_id", velden.klant_id);
    form.append("tekst", velden.tekst);
    if (velden.onderwerp) form.append("onderwerp", velden.onderwerp);
    if (velden.foto) form.append("foto", velden.foto);
    await apiClient.post("/api/werkplek/proefbericht", form);
  },
};

function foutTekst(fout: unknown): string {
  const detail = (fout as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === "string" ? detail : "Inzetten lukte niet. Probeer het opnieuw.";
}

export function Proefbericht() {
  const [open, setOpen] = useState(false);
  const [gemeld, setGemeld] = useState<string | null>(null);
  // Alleen staging geeft hier 200; 404 (productie) of een fout: geen knop.
  const klanten = useQuery({
    queryKey: ["v6", "proefbericht", "klanten"],
    queryFn: proefApi.klanten,
    retry: false,
    staleTime: 60_000,
  });
  // Alleen een echte klantenlijst telt (geen HTML van een proxy bij een storing).
  if (!Array.isArray(klanten.data?.klanten) || !Array.isArray(klanten.data?.kanalen)) return null;

  return (
    <div className="v6-proefbericht">
      <button
        type="button"
        className="v6-knop proefknop"
        aria-haspopup="dialog"
        onClick={() => {
          setGemeld(null);
          setOpen(true);
        }}
      >
        Proefbericht
        <small>staging</small>
      </button>
      {gemeld && (
        <p className="gemeld" role="status">
          {gemeld}
        </p>
      )}
      {open && (
        <Formulier
          data={klanten.data as ProefKlanten}
          onSluit={() => setOpen(false)}
          onIngezet={(melding) => {
            setGemeld(melding);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function Formulier({
  data,
  onSluit,
  onIngezet,
}: {
  data: ProefKlanten;
  onSluit: () => void;
  onIngezet: (melding: string) => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const [kanaal, setKanaal] = useState<ProefKanaal>("mail");
  const [klantId, setKlantId] = useState(data.klanten[0]?.id ?? "");
  const [onderwerp, setOnderwerp] = useState("");
  const [tekst, setTekst] = useState("");
  const [foto, setFoto] = useState<File | null>(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  useEffect(() => {
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape") onSluit();
    };
    document.addEventListener("keydown", toets);
    return () => document.removeEventListener("keydown", toets);
  }, [onSluit]);

  const klant = data.klanten.find((k) => k.id === klantId) ?? null;
  const geenAdres =
    kanaal === "mail" && klant && !klant.email
      ? "Deze klant heeft geen e-mailadres."
      : kanaal === "whatsapp" && klant && !klant.mobiel
        ? "Deze klant heeft geen mobiel nummer."
        : null;
  const fotoMag = kanaal !== "mail";
  const compleet =
    Boolean(klant) &&
    !geenAdres &&
    (kanaal === "mail"
      ? onderwerp.trim() !== "" && tekst.trim() !== ""
      : tekst.trim() !== "" || foto !== null);

  const verstuur = async (e: FormEvent) => {
    e.preventDefault();
    if (!klant || !compleet || bezig) return;
    setBezig(true);
    setFout(null);
    try {
      await proefApi.zetIn({
        kanaal,
        klant_id: klant.id,
        tekst: tekst.trim(),
        onderwerp: kanaal === "mail" ? onderwerp.trim() : undefined,
        foto: fotoMag ? foto : null,
      });
      // De zaak verschijnt pas als de inname klaar is; de lijst ververst zelf
      // live, dit is alleen een duwtje.
      void qc.invalidateQueries({ queryKey: v6Sleutels.lijst });
      onIngezet(
        `${KANAAL_WOORD[kanaal]} van ${klant.naam} ingezet. De zaak verschijnt zo in de lijst.`,
      );
    } catch (err) {
      setFout(foutTekst(err));
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="v6-proef-achter" onMouseDown={(e) => e.target === e.currentTarget && onSluit()}>
      <form
        className="v6-proef-venster"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-kop`}
        onSubmit={verstuur}
      >
        <header>
          <h2 id={`${id}-kop`}>Proefbericht</h2>
          <button type="button" className="sluit" onClick={onSluit} aria-label="Sluiten">
            Sluiten
          </button>
        </header>
        <p className="uitleg">
          Een bericht van een nagespeelde klant, door de gewone inname. Alleen op staging; er gaat
          niets naar een echte klant.
        </p>

        <fieldset className="kanalen">
          <legend>Kanaal</legend>
          {data.kanalen.map((k) => (
            <label key={k} className={k === kanaal ? "gekozen" : undefined}>
              <input
                type="radio"
                name={`${id}-kanaal`}
                value={k}
                checked={k === kanaal}
                onChange={() => setKanaal(k)}
              />
              {KANAAL_WOORD[k]}
            </label>
          ))}
        </fieldset>

        <label className="veld">
          <span>Klant</span>
          <select value={klantId} onChange={(e) => setKlantId(e.target.value)}>
            {data.klanten.map((k) => (
              <option key={k.id} value={k.id}>
                {k.naam} ({k.sleutel}
                {k.aantal_orders ? `, ${k.aantal_orders} bestellingen` : ""})
              </option>
            ))}
          </select>
        </label>
        {geenAdres && <p className="v6-fout">{geenAdres}</p>}

        {kanaal === "mail" && (
          <label className="veld">
            <span>Onderwerp</span>
            <input
              type="text"
              value={onderwerp}
              onChange={(e) => setOnderwerp(e.target.value)}
              placeholder="Waar blijft mijn bestelling?"
            />
          </label>
        )}

        <label className="veld">
          <span>Tekst</span>
          <textarea
            rows={5}
            value={tekst}
            maxLength={data.max_tekst}
            onChange={(e) => setTekst(e.target.value)}
            placeholder="Wat de klant schrijft"
          />
        </label>

        {fotoMag ? (
          <label className="veld">
            <span>Foto (optioneel)</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(e) => setFoto(e.target.files?.[0] ?? null)}
            />
          </label>
        ) : (
          <p className="uitleg">
            Een foto kan bij chat en WhatsApp. Bij mail niet: mailbijlagen komen pas bij het
            bekijken uit Gmail, en Gmail is op staging dicht.
          </p>
        )}

        {fout && (
          <p className="v6-fout" role="alert">
            {fout}
          </p>
        )}

        <footer>
          <button type="button" className="v6-knop" onClick={onSluit}>
            Annuleren
          </button>
          <button type="submit" className="v6-knop acc" disabled={!compleet || bezig}>
            {bezig ? "Bezig" : "Bericht inzetten"}
          </button>
        </footer>
      </form>
    </div>
  );
}
